import React, { useState, useEffect } from 'react';
import { devError } from '../lib/devLog';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import PriceTag from '../components/PriceTag';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import RequestArtistPackModal from '../components/RequestArtistPackModal';
import ArtistHeroScene from '../components/ArtistHeroScene';
import { splitBioToParagraphs } from '../lib/splitBioToParagraphs';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Share2, MessageCircle, Facebook, Twitter, Mail, Link as LinkIcon } from 'lucide-react';
import ShirleyBakerPage from './ShirleyBakerPage';
import NotFoundPage from './NotFoundPage';
import PageSectionRenderer from '../components/PageSectionRenderer';

// Bespoke artist hubs override the generic profile but keep the
// /artist/:key URL — so the artist remains discoverable from /artists.
// Keys are kebab-case `slug` values (which is also the URL).
const BESPOKE_ARTIST_PAGES = {
  'shirley-baker': ShirleyBakerPage,
};

// Legacy internal `id` → canonical `slug` map for clean-URL redirects.
// When a visitor lands on /artist/<old-id>, we 301-style redirect them to
// /artist/<slug> so the URL bar never shows the hash suffix.
const LEGACY_ID_TO_SLUG = {
  'shirley-baker-de573754': 'shirley-baker',
};

// Parses simple Markdown-style `[label](url)` inline links inside a bio
// paragraph and returns a React fragment with the link rendered. Internal
// (`/artwork/...`, `/artist/...`) links use react-router's <Link>; external
// links open in a new tab. Anything without a link pattern passes through
// untouched, so we don't regress the 50 other artist bios.
const BIO_LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;
const renderBioInlineLinks = (text) => {
  if (!text || !text.includes('](')) return text;
  const parts = [];
  let lastIndex = 0;
  let m;
  let i = 0;
  // eslint-disable-next-line no-cond-assign
  while ((m = BIO_LINK_RE.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    const [, label, href] = m;
    const internal = href.startsWith('/');
    if (internal) {
      parts.push(
        <Link
          key={`bio-link-${i++}-${m.index}`}
          to={href}
          className="italic underline underline-offset-4 decoration-1 hover:decoration-2 hover:text-black transition"
          data-testid={`bio-inline-link-${href.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
        >
          {label}
        </Link>,
      );
    } else {
      parts.push(
        <a
          key={`bio-link-${i++}-${m.index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="italic underline underline-offset-4 decoration-1 hover:decoration-2 hover:text-black transition"
        >
          {label}
        </a>,
      );
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Rotates a single artist's hero through 3 pre-rendered AI scenes (loaded
// from /artist-heroes/manifest.json). Falls back to the CSS-overlay style
// if scenes haven't been generated yet for this artist.
const ArtistHeroSceneRotator = ({ artist, artwork, className = '' }) => {
  const [idx, setIdx] = React.useState(0);
  const [aiScenes, setAiScenes] = React.useState(null);
  const isSculpture = (artwork?.category || '').toLowerCase() === 'sculpture';
  const captionLabel = isSculpture ? 'Displayed in' : 'Hanging in';

  React.useEffect(() => {
    let cancel = false;
    fetch('/artist-heroes/manifest.json')
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => {
        if (cancel || !m) return;
        const entry = m[artist?.id];
        if (entry && Array.isArray(entry.scenes)) {
          // Only switch to AI mode when ALL 3 scenes are present — partial sets
          // would rotate to themselves, which looks broken. CSS-overlay fallback
          // gives a consistent 3-scene rotation for the few artists whose source
          // images couldn't be processed (e.g. blocked by Gemini's content filter).
          const allPresent = [1, 3].includes(entry.scenes.length) && entry.scenes.every(Boolean);
          if (allPresent) setAiScenes(entry.scenes);
        }
      })
      .catch(() => {});
    return () => { cancel = true; };
  }, [artist?.id]);

  React.useEffect(() => {
    const count = aiScenes ? aiScenes.length : 3;
    const t = setInterval(() => setIdx((i) => (i + 1) % count), 4000);
    return () => clearInterval(t);
  }, [aiScenes]);

  // AI mode — display pre-rendered scenes
  if (aiScenes && aiScenes.length > 0) {
    return (
      <div className={`relative w-full overflow-hidden bg-black ${className}`} data-testid="artist-hero-ai">
        <style>{`
          @keyframes zog-kenburns { from { transform: scale(1) } to { transform: scale(1.06) } }
        `}</style>
        {aiScenes.map((src, i) => {
          const active = i === idx;
          return (
            <img
              // Remount the active img every cycle so the Ken Burns animation restarts cleanly.
              key={`${src}-${active ? idx : 'idle'}`}
              src={src}
              alt={`${artist.name} hero scene ${i + 1}`}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
              style={{
                opacity: active ? 1 : 0,
                animation: active ? 'zog-kenburns 4s ease-out forwards' : 'none',
                transformOrigin: 'center center',
              }}
            />
          );
        })}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
      </div>
    );
  }

  // CSS-overlay fallback
  return (
    <div className={`relative w-full overflow-hidden ${className}`}>
      <style>{`
        @keyframes zog-kenburns-css { from { transform: scale(1) } to { transform: scale(1.05) } }
      `}</style>
      {[0, 1, 2].map((i) => {
        const active = i === idx;
        return (
          <div
            key={`${i}-${active ? idx : 'idle'}`}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{
              opacity: active ? 1 : 0,
              animation: active ? 'zog-kenburns-css 4s ease-out forwards' : 'none',
              transformOrigin: 'center center',
            }}
          >
            <ArtistHeroScene artist={artist} artwork={artwork} sceneIndex={i} className="h-full" />
          </div>
        );
      })}
    </div>
  );
};

const ArtistProfilePage = () => {
  const { id } = useParams();
  const [artist, setArtist] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [customSections, setCustomSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPackModal, setShowPackModal] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  const shareArtist = (platform) => {
    if (!artist) return;
    const shareUrl = `${window.location.origin}/artists/${artist.slug || artist.id}`;
    const title = `${artist.name} — Zebra One Gallery`;
    const message = `${artist.name} at Zebra One Gallery — ${shareUrl}`;
    const urls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(message)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Have a look at ${artist.name}'s portfolio: ${shareUrl}`)}`,
    };
    if (platform === 'copy') {
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    } else if (platform === 'whatsapp' || platform === 'email') {
      window.open(urls[platform], '_blank', 'noopener,noreferrer');
    } else {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };
  const [notFoundKind, setNotFoundKind] = useState(null); // 'gone' | 'missing' | null

  useEffect(() => {
    fetchArtist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // After the artworks render (which is when the #magnani-vol-iii anchor
  // actually exists in the DOM), scroll to any hash in the URL. Runs once
  // per artwork-load — visitors coming from the Home Vol III trailer land
  // exactly on the right band.
  useEffect(() => {
    if (loading || artworks.length === 0) return;
    if (!window.location.hash) return;
    const anchor = document.getElementById(window.location.hash.slice(1));
    if (!anchor) return;
    // Give the grid a beat to lay out (framer-motion staggered reveals),
    // then smooth-scroll into view.
    const timer = setTimeout(() => {
      anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, artworks.length]);

  const fetchArtist = async () => {
    try {
      const response = await fetch(`${API_URL}/api/artists/${id}`);
      if (!response.ok) {
        // Check tombstone for 410 Gone
        try {
          const tomb = await fetch(`${API_URL}/api/deleted/artist/${id}`);
          if (tomb.ok) {
            const td = await tomb.json();
            if (td.deleted) {
              setNotFoundKind('gone');
              setLoading(false);
              return;
            }
          }
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') console.debug('[artist] alias lookup failed:', err);
        }
        setNotFoundKind('missing');
        setLoading(false);
        return;
      }
      const data = await response.json();
      setArtist(data);

      // Fetch artworks by this artist using the resolved internal UUID id
      // (NOT the URL slug — artworks store the artistId UUID, so querying
      // by `?artistId=<slug>` returns zero results and pre-render snapshots
      // ship "0 works" to Googlebot — catastrophic for SEO).
      const resolvedId = data?.id || id;
      const artworksRes = await fetch(`${API_URL}/api/artworks?artistId=${resolvedId}&limit=500`);
      const artworksData = await artworksRes.json();
      setArtworks(artworksData);

      // Admin-authored custom sections (Page Manager → Artist pages).
      // 404 is the expected shape for artists without custom content — the
      // default template renders in that case.
      try {
        const pageRes = await fetch(`${API_URL}/api/pages/artist-${data.slug || id}`);
        if (pageRes.ok) {
          const pageData = await pageRes.json();
          if (Array.isArray(pageData.sections)) {
            setCustomSections(pageData.sections);
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.debug('[artist] no custom sections:', err);
      }

      setLoading(false);
    } catch (error) {
      devError('Error fetching artist:', error);
      setNotFoundKind('missing');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground font-sans text-sm">Loading artist...</p>
        </div>
      </div>
    );
  }

  if (notFoundKind) {
    return <NotFoundPage deleted={notFoundKind === 'gone'} />;
  }

  if (!artist) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h2 className="font-serif text-3xl mb-4">Artist Not Found</h2>
          <Link to="/artists" className="text-sm font-sans hover:underline">
            ← Back to Artists
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen">
      <SEO
        title={artist.seoTitle || (
          `${artist.name} | Zebra One Gallery`.length <= 60
            ? `${artist.name} | Zebra One Gallery`
            : `${artist.name} | Zebra One`
        )}
        description={artist.seoDescription || `${artist.name}${artist.birthYear ? ` (b. ${artist.birthYear})` : ''}. ${(artist.biography || artist.bio || '').slice(0, 160)} View available works at Zebra One Gallery, Hampstead.`}
        keywords={artist.seoKeywords || undefined}
        image={artist.imageUrl ? (artist.imageUrl.startsWith('http') ? artist.imageUrl : `${API_URL}${artist.imageUrl}`) : undefined}
        canonical={typeof window !== 'undefined' ? window.location.href.split('?')[0] : undefined}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: artist.name,
          ...(artist.birthYear && { birthDate: String(artist.birthYear) }),
          ...(artist.nationality && { nationality: artist.nationality }),
          jobTitle: 'Artist',
          description: (artist.biography || artist.bio || '').slice(0, 500),
          ...(artist.imageUrl && { image: artist.imageUrl.startsWith('http') ? artist.imageUrl : `${API_URL}${artist.imageUrl}` }),
          worksFor: {
            '@type': 'ArtGallery',
            name: 'Zebra One Gallery',
            url: 'https://www.zebraonegallery.com',
            address: {
              '@type': 'PostalAddress',
              streetAddress: "1 Perrin's Court",
              addressLocality: 'Hampstead',
              addressRegion: 'London',
              postalCode: 'NW3 1QS',
              addressCountry: 'GB',
            },
          },
          ...(artworks.length > 0 && {
            workExample: artworks.slice(0, 8).map((w) => ({
              '@type': 'VisualArtwork',
              name: w.title,
              dateCreated: w.year,
              ...(w.medium && { artMedium: w.medium }),
            })),
          }),
        }}
      />
      <Header />

      {/* Cinematic 3-scene hero — uses pre-rendered AI scenes if available,
          falls back to the CSS overlay otherwise */}
      {(() => {
        const heroArt =
          artworks.find((a) => a.featured && a.imageUrl) ||
          artworks.find((a) => a.available && a.imageUrl) ||
          artworks.find((a) => a.imageUrl);
        if (!heroArt) return null;
        return (
          <ArtistHeroSceneRotator
            artist={artist}
            artwork={heroArt}
            className="aspect-video sm:aspect-auto sm:h-[60vh] sm:min-h-[420px] mt-16"
          />
        );
      })()}

      {/* Back Button */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="max-w-[1600px] mx-auto px-6 pt-32 pb-8"
      >
        <Link 
          to="/artists"
          className="inline-flex items-center gap-2 text-sm font-sans text-muted-foreground hover:text-foreground transition-colors"
          data-testid="back-to-artists"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Artists
        </Link>
      </motion.div>

      {/* Artist Header */}
      <section className="max-w-[1600px] mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Artist Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div className="relative aspect-square bg-muted rounded-full overflow-hidden">
              {artist.imageUrl ? (
                <img
                  src={artist.imageUrl}
                  alt={artist.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <p className="text-4xl font-serif text-muted-foreground">
                    {artist.name.charAt(0)}
                  </p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Artist Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-7"
          >
            <div className="lg:sticky lg:top-32">
              <div className="flex items-start justify-between gap-4 mb-6">
                <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-foreground tracking-tight">
                  {artist.name}
                </h1>
                <div className="relative shrink-0 mt-3">
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-foreground/30 hover:border-foreground rounded-full text-xs sm:text-sm transition-colors font-sans"
                    data-testid="artist-share-btn"
                    aria-label="Share artist"
                  >
                    <Share2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Share</span>
                  </button>
                  <AnimatePresence>
                    {showShareMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="absolute right-0 top-full mt-2 bg-background border border-border rounded-lg shadow-xl p-2 w-48 z-20"
                      >
                        <button
                          onClick={() => shareArtist('whatsapp')}
                          data-testid="artist-share-whatsapp-btn"
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted rounded text-sm font-sans"
                        >
                          <MessageCircle className="w-4 h-4 text-[#25D366]" />
                          WhatsApp
                        </button>
                        <button onClick={() => shareArtist('facebook')} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted rounded text-sm font-sans">
                          <Facebook className="w-4 h-4" />
                          Facebook
                        </button>
                        <button onClick={() => shareArtist('twitter')} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted rounded text-sm font-sans">
                          <Twitter className="w-4 h-4" />
                          Twitter
                        </button>
                        <button onClick={() => shareArtist('email')} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted rounded text-sm font-sans">
                          <Mail className="w-4 h-4" />
                          Email
                        </button>
                        <hr className="my-2 border-border" />
                        <button onClick={() => shareArtist('copy')} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted rounded text-sm font-sans">
                          <LinkIcon className="w-4 h-4" />
                          Copy link
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 mb-8">
                {artist.nationality && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-sans mb-1">
                      Nationality
                    </p>
                    <p className="text-base font-sans text-foreground">{artist.nationality}</p>
                  </div>
                )}
                {artist.birthYear && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-sans mb-1">
                      Born
                    </p>
                    <p className="text-base font-sans text-foreground">{artist.birthYear}</p>
                  </div>
                )}
              </div>

              {artist.biography ? (
                <div className="border-t border-border pt-8">
                  <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-sans mb-4">
                    Biography
                  </h2>
                  <div className="font-sans text-base text-foreground leading-relaxed space-y-4" data-testid="artist-biography">
                    {splitBioToParagraphs(artist.biography).map((para, i) => (
                      <p key={`bio-${i}-${para.slice(0, 24)}`}>{renderBioInlineLinks(para)}</p>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="border-t border-border pt-8" data-testid="artist-bio-placeholder">
                  <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-sans mb-4">
                    Biography
                  </h2>
                  <p className="font-sans text-base text-muted-foreground leading-relaxed mb-4">
                    A full biography for {artist.name} will be added shortly. In the meantime,
                    Gabrielle and the team would be happy to send over a press pack or answer
                    any questions about their work.
                  </p>
                  <button
                    onClick={() => setShowPackModal(true)}
                    className="inline-block px-6 py-3 border border-foreground text-foreground font-sans text-sm uppercase tracking-[0.2em] hover:bg-foreground hover:text-background transition-colors"
                    data-testid="artist-bio-contact-cta"
                  >
                    Request artist pack
                  </button>
                </div>
              )}

              {/* Exhibitions — structured list grouped by year. Stored on the
                  artist record as `exhibitions: [{ year, items: [...] }]`. */}
              {Array.isArray(artist.exhibitions) && artist.exhibitions.length > 0 && (
                <div className="border-t border-border pt-8" data-testid="artist-exhibitions">
                  <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-sans mb-6">
                    Selected Exhibitions
                  </h2>
                  <dl className="space-y-5 font-sans text-base text-foreground">
                    {artist.exhibitions.map((group) => (
                      <div key={`ex-${group.year}`} className="grid grid-cols-[5rem_1fr] gap-4 sm:gap-6">
                        <dt className="font-serif text-xl text-foreground/80 leading-snug">{group.year}</dt>
                        <dd>
                          <ul className="space-y-1.5 list-none">
                            {(group.items || []).map((item, j) => (
                              <li key={`ex-item-${group.year}-${j}`} className="leading-snug">{item}</li>
                            ))}
                          </ul>
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              {/* Education — same shape as exhibitions for symmetry. */}
              {Array.isArray(artist.education) && artist.education.length > 0 && (
                <div className="border-t border-border pt-8" data-testid="artist-education">
                  <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-sans mb-6">
                    Education
                  </h2>
                  <ul className="space-y-3 font-sans text-base text-foreground list-none">
                    {artist.education.map((item, i) => (
                      <li key={`edu-${i}`} className="leading-snug">{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Admin-authored custom sections (Page Manager). Renders NOTHING
          when this artist has no custom content — keeps the standard
          template intact for every other artist. */}
      <PageSectionRenderer sections={customSections} />

      {/* Artist's Works */}
      <section className="max-w-[1600px] mx-auto px-6 py-20 border-t border-border">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-end justify-between mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-foreground">
              Available Works
            </h2>
            <p className="text-sm font-sans text-muted-foreground">
              {artworks.length} {artworks.length === 1 ? 'work' : 'works'}
            </p>
          </div>

          {artworks.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-sans text-muted-foreground">
                No works currently available from this artist.
              </p>
            </div>
          ) : (
            (() => {
              // If ≥2 works carry a `series` tag, split the grid into series bands.
              // Order: "Daily Bat – Volume I / II / III / Multiples" then any others
              // then a final "Other Works" bucket for untagged pieces.
              const grouped = artworks.reduce((acc, w) => {
                const key = w.series || '__untagged__';
                (acc[key] = acc[key] || []).push(w);
                return acc;
              }, {});
              const seriesKeys = Object.keys(grouped).filter((k) => k !== '__untagged__');
              const shouldGroup = seriesKeys.length >= 2;

              const SERIES_ORDER = [
                'Daily Bat – Volume I',
                'Daily Bat – Volume II',
                'Daily Bat – Volume III',
                'Daily Bat – Multiples',
              ];
              const sortedSeries = [
                ...SERIES_ORDER.filter((s) => grouped[s]),
                ...seriesKeys.filter((s) => !SERIES_ORDER.includes(s)).sort(),
              ];

              const bands = shouldGroup
                ? [
                    ...sortedSeries.map((s) => ({ label: s, works: grouped[s] })),
                    ...(grouped.__untagged__ ? [{ label: 'Other Works', works: grouped.__untagged__ }] : []),
                  ]
                : [{ label: null, works: artworks }];

              const renderCard = (artwork, index) => (
                <motion.div
                  key={artwork.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.6,
                    delay: Math.min(index, 8) * 0.05,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  data-testid={`artwork-${artwork.id}`}
                >
                  <Link to={`/artwork/${artwork.slug || artwork.id}`} className="group block">
                    <motion.div
                      className="relative aspect-[3/4] bg-muted mb-4 overflow-hidden"
                      whileHover={{ scale: 0.98 }}
                      transition={{ duration: 0.4 }}
                    >
                      {artwork.imageUrl ? (
                        <img
                          src={artwork.imageUrl}
                          alt={artwork.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <p className="text-xs text-muted-foreground font-sans">Image Coming Soon</p>
                        </div>
                      )}

                      {!artwork.available && (
                        <div className="absolute top-4 right-4 px-3 py-1 bg-foreground text-background text-xs font-sans uppercase tracking-wider">
                          Sold
                        </div>
                      )}
                    </motion.div>

                    <div className="space-y-1">
                      <h3 className="font-serif text-xl text-foreground group-hover:text-muted-foreground transition-colors">
                        {artwork.title}
                      </h3>
                      {artwork.year && (
                        <p className="text-sm text-muted-foreground font-sans">{artwork.year}</p>
                      )}
                      <PriceTag
                        priceDisplay={artwork.priceDisplay}
                        vatScheme={artwork.vatScheme || 'standard'}
                        className="text-sm font-sans text-foreground pt-2 block"
                      />
                    </div>
                  </Link>
                </motion.div>
              );

              return bands.map((band, bIdx) => {
                // Stable, human-readable anchor slug so external pages can
                // deep-link into a specific series band, e.g.
                //   /artist/sebastian-magnani#magnani-vol-iii
                let anchorId;
                if (band.label === 'Daily Bat – Volume I') anchorId = 'magnani-vol-i';
                else if (band.label === 'Daily Bat – Volume II') anchorId = 'magnani-vol-ii';
                else if (band.label === 'Daily Bat – Volume III') anchorId = 'magnani-vol-iii';
                else if (band.label === 'Daily Bat – Multiples') anchorId = 'magnani-multiples';

                return (
                  <div
                    key={band.label || `band-${bIdx}`}
                    id={anchorId}
                    className={bIdx > 0 ? 'mt-24 scroll-mt-32' : 'scroll-mt-32'}
                    data-testid={`artist-works-band-${(band.label || 'all').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                  >
                    {band.label && (
                      <div className="flex items-end justify-between mb-8 border-t border-border pt-8">
                        <h3 className="font-serif text-2xl sm:text-3xl text-foreground">
                          {band.label}
                        </h3>
                        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-sans">
                          {band.works.length} {band.works.length === 1 ? 'work' : 'works'}
                        </p>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                      {band.works.map(renderCard)}
                    </div>
                  </div>
                );
              });
            })()
          )}
        </motion.div>
      </section>

      <Footer />

      <RequestArtistPackModal
        open={showPackModal}
        onClose={() => setShowPackModal(false)}
        artist={artist}
      />
    </div>
  );
};

// Route-level wrapper — handles three URL flavours so we always land on the
// clean kebab-case slug:
//   1. /artist/<slug>           → bespoke page (if registered) or generic profile
//   2. /artist/<legacy-id>      → permanent client-side redirect to /artist/<slug>
//   3. /artist/<unknown-uuid>   → look up the artist; if it has a slug, redirect
//
// All bespoke-page checks key on `slug`, not the internal `id`, so adding a
// new editorial hub is just a matter of dropping `{ '<slug>': Page }` into
// BESPOKE_ARTIST_PAGES above. Hook ordering inside ArtistProfilePage is
// untouched because this dispatch lives in a wrapper component.
const ArtistProfileRouter = () => {
  const { id: routeKey } = useParams();
  const navigate = useNavigate();
  const [resolved, setResolved] = useState(null);   // resolved slug (or false = not found, or null = pending)

  // Hard-coded fast path — known legacy ids redirect synchronously so we
  // never even flash the old URL.
  useEffect(() => {
    const direct = LEGACY_ID_TO_SLUG[routeKey];
    if (direct && direct !== routeKey) {
      navigate(`/artist/${direct}`, { replace: true });
      return;
    }
    // If the key already looks like a clean slug (no spaces, all lowercase
    // alnum/-, and not a UUID-style hex string) we don't need to round-trip.
    const looksLikeSlug = /^[a-z0-9-]+$/.test(routeKey) && !/^[0-9a-f]{8}-[0-9a-f]{4}-/.test(routeKey);
    if (looksLikeSlug && !/-[0-9a-f]{8}$/.test(routeKey)) {
      setResolved(routeKey);
      return;
    }
    // Otherwise resolve via API — get the artist by id, then redirect to
    // its slug if one exists.
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/artists/${routeKey}`);
        if (!res.ok) {
          if (!cancelled) setResolved(false);
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        if (data?.slug && data.slug !== routeKey) {
          navigate(`/artist/${data.slug}`, { replace: true });
        } else {
          setResolved(data?.slug || routeKey);
        }
      } catch {
        if (!cancelled) setResolved(routeKey);  // fall through to render
      }
    })();
    return () => { cancelled = true; };
  }, [routeKey, navigate]);

  if (resolved === null) {
    // Brief loading state while we resolve a legacy id.
    return null;
  }
  const Bespoke = BESPOKE_ARTIST_PAGES[resolved];
  if (Bespoke) return <Bespoke key={resolved} />;
  return <ArtistProfilePage key={resolved || routeKey} />;
};

export default ArtistProfileRouter;
